
'use client';

import *as React from 'react';
import { auth, db } from '@/lib/firebase';
import { signedOutHome } from '@/lib/native';
import { signOut, onAuthStateChanged, type User } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useRouter, usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LogOut, User as UserIcon, Edit3, LogIn, Shield, Link as LinkIcon, ChevronDown, Crown, Briefcase } from 'lucide-react';
import type { FirebaseUserProfile } from '@/lib/types';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { getMembershipLabel, isPaidLikeMembership } from '@/lib/membership';
import { getCookie, eraseCookie } from '@/lib/cookies';
import { authPageHref, safeLandingPath } from '@/lib/agent-link';

// Shared with MobileTabBar's Account menu so the two dropdowns stay in sync.
export const menuPanelClass =
  'w-72 bg-[#f5f2ed] border border-[#18191B]/20 rounded-none shadow-[0_10px_30px_rgba(24,25,27,0.16)] p-0 text-[#18191B]';
const menuLabelClass = 'px-5 py-4 font-normal';
const menuNameClass = 'text-[15px] font-medium leading-none text-[#18191B]';
const menuEmailClass = 'text-sm leading-none text-[#6b6a64] mt-2';
export const menuSeparatorClass = 'bg-[#18191B]/18';
export const menuItemClass =
  'px-5 py-3.5 text-[15px] text-[#18191B]/92 hover:bg-[#18191B]/5 focus:bg-[#18191B]/5 cursor-pointer';
export const menuIconClass = 'mr-3 h-[18px] w-[18px] text-[#b99a5a]';

const getLocalStorage = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    console.error("[AuthButton] Local storage is unavailable on get.", { error: e });
    return null;
  }
};

const eraseLocalStorage = (key: string) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    console.error("[AuthButton] Local storage is unavailable on erase.", { error: e });
  }
};


export default function AuthButton() {
  const [currentUser, setCurrentUser] = React.useState<User | null>(null);
  const [userProfile, setUserProfile] = React.useState<FirebaseUserProfile | null>(null);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isAgent, setIsAgent] = React.useState(false);
  const [isPaidMember, setIsPaidMember] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const router = useRouter();
  const pathname = usePathname();
  const { toast } = useToast();
  const isMobile = useIsMobile();


  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsLoading(true);
      if (user) {
        setCurrentUser(user);
        
        const idTokenResult = await user.getIdTokenResult(true);
        setIsAdmin(!!idTokenResult.claims.isAdmin);

        const userDocRef = doc(db, 'users', user.uid);
        const { getNormalizedUserProfile } = await import('@/lib/actions');
        const normalizedProfile = await getNormalizedUserProfile(idTokenResult.token);

        if (normalizedProfile) {
          const profileData = normalizedProfile as FirebaseUserProfile;

          // Set by the sign-up page before this visitor signed in to an existing account instead.
          // Nothing here consumes it, and the next account made in this browser would inherit it.
          eraseCookie('agentId');

          // Repair profiles that were created without the auth identity fields
          // (e.g. an older partial write). Without this the admin user list and
          // notification emails show an empty name/email forever.
          const identityRepair: Record<string, unknown> = {};
          if (!profileData.email && user.email) identityRepair.email = user.email;
          if (!profileData.name && user.displayName) identityRepair.name = user.displayName;
          if (!profileData.photoURL && user.photoURL) identityRepair.photoURL = user.photoURL;
          if (Object.keys(identityRepair).length > 0) {
            try {
              await setDoc(userDocRef, { ...identityRepair, updatedAt: serverTimestamp() }, { merge: true });
              Object.assign(profileData, identityRepair);
            } catch (error) {
              console.error('[AuthButton] Error repairing user profile identity:', error);
            }
          }

          // Check if user's email matches any agent email via server action
          let isUserAgent = !!profileData.isAgent;
          if (!isUserAgent && user.email) {
            try {
              const { checkAndSetAgentStatus } = await import('@/lib/actions');
              isUserAgent = await checkAndSetAgentStatus(idTokenResult.token);
              if (isUserAgent) {
                profileData.isAgent = true;
              }
            } catch (error) {
              console.error('[AuthButton] Error checking agent status:', error);
            }
          }

          // An agent whose address is not verified yet gets no agent access, but still needs a way to
          // the dashboard, where they can send themselves the verification email.
          const emailVerified = idTokenResult.claims.email_verified === true;
          let showAgentLink = isUserAgent;
          if (!isUserAgent && user.email && !emailVerified) {
            try {
              const { getAgentAccessStatus } = await import('@/lib/actions');
              showAgentLink = (await getAgentAccessStatus(idTokenResult.token)) === 'verify_email';
            } catch (error) {
              console.error('[AuthButton] Error checking agent access:', error);
            }
          }

          // A password account referred by an agent only gets the welcome email once its address is
          // verified, which happens outside the site; the first sign-in after that sends it.
          if (profileData.agentWelcomeEmailPending && emailVerified) {
            import('@/lib/actions')
              .then(({ sendWelcomeEmailForAgent }) => sendWelcomeEmailForAgent(idTokenResult.token))
              .catch(error => console.error('[AuthButton] Error sending agent welcome email:', error));
          }
          // The same goes for the membership an agent link gives (e.g. Storebrand's links).
          if (profileData.agentLinkTierPending && emailVerified) {
            try {
              const { claimAgentLinkMembership } = await import('@/lib/actions');
              const { granted } = await claimAgentLinkMembership(idTokenResult.token);
              if (granted) {
                const claimed = await getNormalizedUserProfile(idTokenResult.token);
                if (claimed) Object.assign(profileData, claimed);
              }
            } catch (error) {
              console.error('[AuthButton] Error granting agent link membership:', error);
            }
          }
          
          setUserProfile(profileData);
          
          const isPaid = isPaidLikeMembership(profileData.membershipTier);
          setIsPaidMember(isPaid);
          const isAgentReferred = !!profileData.agentId;
          const isProfileComplete = !!profileData.isProfileComplete;
          const isUserAdmin = !!profileData.isAdmin;
          setIsAgent(showAgentLink);

          // REDIRECTION LOGIC FOR LOGGED-IN USERS
          // Priority 1: Complete profile first (regardless of membership status)
          if (!isProfileComplete && !pathname.startsWith('/auth')) {
            toast({ title: "Profile Incomplete", description: "Please complete your profile information." });
            router.push('/auth/complete-profile');
          }
          // Priority 2: Then check membership (only for non-admin, non-referred free users)
          else if (!isPaid && !isAgentReferred && !isUserAdmin && isProfileComplete) {
              if (pathname !== '/membership' && !pathname.startsWith('/auth')) {
                  router.push('/membership');
              }
          }

        } else {
          // New user signup flow
          try {
            const agentId = getCookie('agentId');
            const marketingConsent = getLocalStorage('marketingConsent') === 'true';

            // The server creates the document: isAdmin, membershipTier and agentId decide what the
            // account is trusted with, so a browser is no longer allowed to write them.
            const { createUserProfile } = await import('@/lib/actions');
            await createUserProfile(idTokenResult.token, { agentId, marketingConsent });
            const created = await getDoc(userDocRef);
            setUserProfile(created.data() as FirebaseUserProfile);
            setIsPaidMember(false);
            
            if (agentId) eraseCookie('agentId');
            eraseLocalStorage('marketingConsent');

            // REDIRECTION FOR NEW USERS
            // All new users must complete their profile first
            if (pathname !== '/auth/complete-profile') {
              toast({ title: "Welcome!", description: "Please complete your profile to continue." });
              router.push('/auth/complete-profile');
            }
          } catch (error) {
            console.error('[AuthButton] Error creating user profile:', error);
            toast({ 
              title: "Profile Creation Failed", 
              description: "Failed to create your profile. Please try refreshing the page or contact support.", 
              variant: "destructive" 
            });
            // Still set basic user state so they can try again
            setUserProfile({
              uid: user.uid,
              email: user.email,
              name: user.displayName || null,
              photoURL: user.photoURL || null,
              isProfileComplete: false,
              isAdmin: false,
              agentId: null,
              marketingConsent: false,
              membershipTier: 'free',
            } as FirebaseUserProfile);
            setIsPaidMember(false);
          }
        }
      } else {
        // For logged-out users, the redirection is now handled in the main Header component.
        // This component just needs to clear its state.
        setCurrentUser(null);
        setUserProfile(null);
        setIsAdmin(false);
        setIsPaidMember(false);
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [router, pathname, toast]);


  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      setCurrentUser(null); // Clear user state immediately
      router.push(signedOutHome()); // The front page in a browser, the login in the app
      toast({ title: "Signed Out", description: "You have been successfully signed out."});
    } catch (error) {
      console.error('[AuthButton] Error signing out:', { error });
      toast({ title: "Sign-out Error", description: "Could not sign out.", variant: "destructive"});
    }
  };

  // A visitor on the sign-up or sign-in page may be on their way somewhere (e.g. a partner link's offer)
  // and keeps going there after signing in. Other pages' query strings say nothing about this.
  // Read on click, since useSearchParams would need a Suspense boundary in the header.
  const handleLogin = () => {
    if (!pathname.startsWith('/auth')) {
      router.push('/auth/login');
      return;
    }
    const query = new URLSearchParams(window.location.search);
    router.push(authPageHref('/auth/login', {
      redirectUrl: safeLandingPath(query.get('redirect_url')),
      agentId: query.get('agentId'),
    }));
  };

  const MobileAuthDisplay = () => (
    <div className="w-full">
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full justify-between border-primary-foreground/50 text-primary-foreground bg-primary/20 hover:bg-primary/40 min-h-11 h-auto py-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                        <Avatar className="h-6 w-6">
                            <AvatarImage src={userProfile?.photoURL || undefined} alt={userProfile?.name || 'User'}/>
                            <AvatarFallback>{userProfile?.name?.charAt(0) || currentUser?.email?.charAt(0) || 'U'}</AvatarFallback>
                        </Avatar>
                        <span className="truncate max-w-[120px]">{userProfile?.name || currentUser?.email}</span>
                        {userProfile?.membershipTier && userProfile.membershipTier !== 'free' && (
                          <span className="inline-flex items-center rounded-none border border-[#daba7b]/40 bg-[#daba7b]/15 px-2 py-0.5 text-[10px] font-medium tracking-wide text-[#f1d8a5] max-w-full">
                            <Crown className="mr-1 h-2.5 w-2.5" />
                            <span className="truncate">{getMembershipLabel(userProfile.membershipTier)}</span>
                          </span>
                        )}
                    </div>
                    <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className={menuPanelClass} align="end" forceMount>
                {/* Menu items are the same as desktop */}
                 <DropdownMenuLabel className={menuLabelClass}>
                    <p className={menuNameClass}>{userProfile?.name || 'Nordic User'}</p>
                    <p className={menuEmailClass}>{currentUser?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className={menuSeparatorClass}/>
                {isAdmin && (
                    <DropdownMenuItem onClick={() => router.push('/admin')} className={menuItemClass}>
                        <Shield className={menuIconClass} />
                        <span>Admin Dashboard</span>
                    </DropdownMenuItem>
                )}
                {isAgent && (
                    <DropdownMenuItem onClick={() => router.push('/agent-dashboard')} className={menuItemClass}>
                        <Briefcase className={menuIconClass} />
                        <span>Agent Dashboard</span>
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => router.push('/auth/complete-profile')} className={menuItemClass}>
                    <Edit3 className={menuIconClass} />
                    <span>Edit Profile</span>
                </DropdownMenuItem>
                {isPaidMember && (
                    <DropdownMenuItem onClick={() => router.push('/subscription')} className={menuItemClass}>
                        <Crown className={menuIconClass} />
                        <span>My Subscription</span>
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => router.push('/bookings')} className={menuItemClass}>
                    <UserIcon className={menuIconClass} />
                    <span>My Bookings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className={menuSeparatorClass}/>
                <DropdownMenuItem onClick={handleSignOut} className={`${menuItemClass} text-[#b8731f] hover:text-[#a76514] focus:text-[#a76514]`}>
                    <LogOut className="mr-3 h-[18px] w-[18px] text-[#d88a12]" />
                    <span>Log out</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    </div>
  );

  const DesktopAuthDisplay = () => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-10 gap-2 rounded-none px-3 py-2.5 text-[13px] font-medium tracking-wide text-[#daba7b] transition-colors hover:bg-white/20"
          >
             <UserIcon className="h-4 w-4 shrink-0" />
             <span className="truncate max-w-[10rem] sm:max-w-[14rem]">{userProfile?.name || currentUser?.email}</span>
             {userProfile?.membershipTier && userProfile.membershipTier !== 'free' && (
               <span className="inline-flex items-center rounded-none border border-[#daba7b]/40 bg-[#daba7b]/15 px-2 py-0.5 text-[10px] font-medium tracking-wide text-[#f1d8a5]">
                 <Crown className="mr-1 h-2.5 w-2.5" />
                 {getMembershipLabel(userProfile.membershipTier)}
               </span>
             )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className={menuPanelClass} align="end" forceMount>
          <DropdownMenuLabel className={menuLabelClass}>
            <div className="flex flex-col space-y-1">
              <p className={menuNameClass}>
                {userProfile?.name || 'Nordic User'}
              </p>
              <p className={menuEmailClass}>
                {currentUser?.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className={menuSeparatorClass}/>
          {isAdmin && (
             <DropdownMenuItem onClick={() => router.push('/admin')} className={menuItemClass}>
                <Shield className={menuIconClass} />
                <span>Admin Dashboard</span>
            </DropdownMenuItem>
          )}
          {isAgent && (
             <DropdownMenuItem onClick={() => router.push('/agent-dashboard')} className={menuItemClass}>
                <Briefcase className={menuIconClass} />
                <span>Agent Dashboard</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => router.push('/auth/complete-profile')} className={menuItemClass}>
            <Edit3 className={menuIconClass} />
            <span>Edit Profile</span>
          </DropdownMenuItem>
          {isPaidMember && (
            <DropdownMenuItem onClick={() => router.push('/subscription')} className={menuItemClass}>
              <Crown className={menuIconClass} />
              <span>My Subscription</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => router.push('/bookings')} className={menuItemClass}>
            <UserIcon className={menuIconClass} />
            <span>My Bookings</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator className={menuSeparatorClass}/>
          <DropdownMenuItem onClick={handleSignOut} className={`${menuItemClass} text-[#b8731f] hover:text-[#a76514] focus:text-[#a76514]`}>
            <LogOut className="mr-3 h-[18px] w-[18px] text-[#d88a12]" />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
  );

  if (isLoading) {
    return <Button variant="ghost" className="text-foreground hover:bg-muted" disabled>Loading...</Button>;
  }

  if (currentUser && userProfile) {
     if (isMobile) return <MobileAuthDisplay />;
     return <DesktopAuthDisplay />;
  }

  return (
    <Button variant="outline" onClick={handleLogin} className={cn(
        "border-primary-foreground/50 text-primary-foreground bg-primary/20 hover:bg-primary/40 rounded-none",
        isMobile && "w-full"
    )}>
      <LogIn className="mr-2 h-4 w-4" /> Login / Sign Up
    </Button>
  );
}
